/* ContextMesh app.js — CM utility object */
const CM = (() => {
  'use strict';

  const Auth = {
    get token()    { return localStorage.getItem('cm_token'); },
    get user()     { try { return JSON.parse(localStorage.getItem('cm_user')); } catch { return null; } },
    get role()     { return (Auth.user || {}).role || null; },
    isLoggedIn()   { return !!Auth.token; },
    isImpersonating() { return !!localStorage.getItem('cm_admin_token'); },
    adminUser()    { try { return JSON.parse(localStorage.getItem('cm_admin_user')); } catch { return null; } },
    _setHtmlAuthClass(authed) {
      const el = document.documentElement;
      el.classList.toggle('is-authed', !!authed);
      el.classList.toggle('is-guest',  !authed);
    },
    save(data)     {
      localStorage.setItem('cm_token',   data.access_token  || data.accessToken || data.token || '');
      localStorage.setItem('cm_user',    JSON.stringify(data.user || {}));
      localStorage.setItem('cm_refresh', data.refresh_token || '');
      Auth._setHtmlAuthClass(true);
    },
    /**
     * Begin impersonation: park the current admin's auth aside, then install
     * the impersonation token in its place.  A banner will appear on the next
     * page render allowing one-click return to the admin session.
     */
    impersonate(data) {
      // Park current admin session (if any).  We snapshot whatever's already
      // there so even non-standard or stale tokens get preserved verbatim.
      const curToken   = localStorage.getItem('cm_token');
      const curUser    = localStorage.getItem('cm_user');
      const curRefresh = localStorage.getItem('cm_refresh');
      if (curToken)   localStorage.setItem('cm_admin_token',   curToken);
      if (curUser)    localStorage.setItem('cm_admin_user',    curUser);
      if (curRefresh) localStorage.setItem('cm_admin_refresh', curRefresh);
      // Install impersonation auth.
      Auth.save(data);
    },
    /**
     * End impersonation: restore the parked admin token and discard the
     * impersonation auth.  Called from the sticky banner's "Return to admin"
     * button (see Auth._renderImpersonationBanner).
     */
    endImpersonation() {
      const adminToken   = localStorage.getItem('cm_admin_token');
      const adminUser    = localStorage.getItem('cm_admin_user');
      const adminRefresh = localStorage.getItem('cm_admin_refresh');
      if (!adminToken) return false;
      localStorage.setItem('cm_token', adminToken);
      if (adminUser)    localStorage.setItem('cm_user',    adminUser);
      else              localStorage.removeItem('cm_user');
      if (adminRefresh) localStorage.setItem('cm_refresh', adminRefresh);
      else              localStorage.removeItem('cm_refresh');
      localStorage.removeItem('cm_admin_token');
      localStorage.removeItem('cm_admin_user');
      localStorage.removeItem('cm_admin_refresh');
      Auth._setHtmlAuthClass(true);
      return true;
    },
    /**
     * Render the "you are impersonating" banner if applicable.  Idempotent —
     * safe to call repeatedly.  Bound to DOMContentLoaded below.
     */
    _renderImpersonationBanner() {
      if (typeof document === 'undefined') return;
      const existing = document.getElementById('cm-impersonation-banner');
      if (!Auth.isImpersonating()) {
        if (existing) existing.remove();
        document.documentElement.classList.remove('is-impersonating');
        return;
      }
      document.documentElement.classList.add('is-impersonating');
      if (existing) return;            // already rendered
      const u     = Auth.user || {};
      const admin = Auth.adminUser() || {};
      const target  = u.name     || u.email     || ('user #' + (u.id || '?'));
      const adminId = admin.name || admin.email || ('admin #' + (admin.id || '?'));
      const bar = document.createElement('div');
      bar.id = 'cm-impersonation-banner';
      bar.className = 'cm-impersonation-banner';
      bar.setAttribute('role', 'status');
      bar.innerHTML =
        '<span class="cm-imp-icon" aria-hidden="true">⚠</span>' +
        '<span class="cm-imp-text">' +
          'Viewing as <strong>' + esc(target) + '</strong>' +
          ' &middot; signed in as <em>' + esc(adminId) + '</em>' +
        '</span>' +
        '<button type="button" class="cm-imp-return">Return to admin</button>';
      bar.querySelector('.cm-imp-return').addEventListener('click', function () {
        if (Auth.endImpersonation()) {
          window.location.href = '/admin';
        }
      });
      document.body.insertBefore(bar, document.body.firstChild);
    },
    clear()        {
      ['cm_token','cm_user','cm_refresh',
       'cm_admin_token','cm_admin_user','cm_admin_refresh'].forEach(k => localStorage.removeItem(k));
      Auth._setHtmlAuthClass(false);
      document.documentElement.classList.remove('is-impersonating');
    },
    requireAuth()  { if (!Auth.isLoggedIn()) { window.location.href = '/login'; return false; } return true; },
    requireGuest() { if (Auth.isLoggedIn()) { window.location.href = '/dashboard'; return false; } return true; }
  };

  async function api(method, path, body) {
    const headers = { 'Content-Type': 'application/json' };
    if (Auth.token) headers['Authorization'] = 'Bearer ' + Auth.token;
    try {
      const res  = await fetch(path, { method, headers, body: body ? JSON.stringify(body) : null });
      const data = await res.json();
      if (res.status === 401) { Auth.clear(); window.location.href = '/login'; return null; }
      return data;
    } catch (e) { return null; }
  }

  const get   = p     => api('GET',    p);
  const post  = (p,b) => api('POST',   p, b);
  const patch = (p,b) => api('PATCH',  p, b);
  const del   = p     => api('DELETE', p);

  function toast(msg, type, ms) {
    type = type || 'default'; ms = ms || 3000;
    var c = document.getElementById('cm-toast-container');
    if (!c) {
      c = document.createElement('div');
      c.id = 'cm-toast-container';
      c.className = 'toast-container';
      document.body.appendChild(c);
    }
    var el = document.createElement('div');
    el.className = 'toast ' + type;
    el.textContent = msg;
    c.appendChild(el);
    setTimeout(function() { el.remove(); }, ms);
  }

  function openModal(id)  { var el = document.getElementById(id); if (el) el.classList.add('open'); }
  function closeModal(id) { var el = document.getElementById(id); if (el) el.classList.remove('open'); }

  document.addEventListener('click', function(e) {
    if (e.target.classList.contains('modal') || e.target.classList.contains('modal-close')) {
      document.querySelectorAll('.modal.open').forEach(function(m) { m.classList.remove('open'); });
    }
  });

  // Header logout button (.js-logout).
  document.addEventListener('click', function(e) {
    var t = e.target.closest && e.target.closest('.js-logout');
    if (!t) return;
    e.preventDefault();
    Auth.clear();
    window.location.href = '/';
  });

  function esc(s) {
    if (s == null) return '';
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  function setLoading(el, on) {
    if (!el) return;
    if (on) { el.dataset.orig = el.innerHTML; el.disabled = true; el.innerHTML = '<span class="spinner" style="width:14px;height:14px;border-width:2px;display:inline-block"></span>'; }
    else    { el.disabled = false; el.innerHTML = el.dataset.orig || ''; }
  }

  // ── Currency display + live conversion (HOTFIX_69) ────────────────────
  // - Default display currency is USD, not EUR. The previous fallback was
  //   silently labelling USD amounts with a EUR sign — a display lie.
  // - When the user picks a non-USD display currency, amounts passed to
  //   CM.fmt.currency (which are USD-denominated by convention) are
  //   actually converted using cached FX rates from /billing/currency/rates.
  // - Rates are cached in localStorage for 1 hour. First page load fetches
  //   them; subsequent loads are instant. If the cache is empty and the
  //   fetch hasn't completed yet, formatting falls back to USD (no symbol
  //   lie). Once rates arrive, components that re-render show the
  //   converted value.

  function getDisplayCurrency() {
    var c = (localStorage.getItem('cm_currency') || 'USD');
    return (c || 'USD').toUpperCase();
  }

  var CCY_SYMBOLS = {
    USD:'$', EUR:'\u20ac', GBP:'\u00a3', CAD:'CA$', AUD:'A$', CHF:'Fr.',
    SEK:'kr', JPY:'\u00a5', BRL:'R$', NOK:'kr', DKK:'kr', PLN:'z\u0142', NZD:'NZ$'
  };

  function ccySymbol(code) {
    code = (code || 'USD').toUpperCase();
    return CCY_SYMBOLS[code] || (code + ' ');
  }

  function getCurrencySymbol() {
    return ccySymbol(getDisplayCurrency());
  }

  var FX = (function(){
    var cacheKey = 'cm_fx_rates_v1';
    var ttlMs    = 60 * 60 * 1000; // 1 hour
    var rates    = { USD: 1 };     // identity always
    var loaded   = false;
    var inflight = null;

    function loadFromCache() {
      try {
        var raw = localStorage.getItem(cacheKey);
        if (!raw) return false;
        var p = JSON.parse(raw);
        if (!p || !p.ts || (Date.now() - p.ts) > ttlMs) return false;
        rates  = p.rates || { USD: 1 };
        rates.USD = 1;
        loaded = true;
        return true;
      } catch(e) { return false; }
    }

    function parseRatesResponse(data) {
      // CurrencyService::getAllRates returns an object keyed by quote, e.g.
      //   { EUR: {quote:'EUR', rate:0.92, fetched_at:'...'}, GBP: {...} }
      // We flatten to { EUR: 0.92, GBP: 0.79, ... } with USD as identity.
      var out = { USD: 1 };
      if (data && typeof data === 'object') {
        Object.keys(data).forEach(function(k){
          var v = data[k];
          if (v && v.rate != null) out[k.toUpperCase()] = parseFloat(v.rate);
          else if (typeof v === 'number') out[k.toUpperCase()] = v;
        });
      }
      return out;
    }

    function fetchFresh() {
      if (inflight) return inflight;
      inflight = fetch('/billing/currency/rates')
        .then(function(r){ return r.ok ? r.json() : null; })
        .then(function(res){
          inflight = null;
          if (res && res.success && res.data) {
            rates = parseRatesResponse(res.data);
            rates.USD = 1;
            loaded = true;
            try {
              localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), rates: rates }));
            } catch(e){}
          }
          return rates;
        })
        .catch(function(){ inflight = null; return rates; });
      return inflight;
    }

    function init() {
      if (loadFromCache()) {
        // Have fresh cache; refresh in background on best-effort basis.
        fetchFresh();
        return Promise.resolve(rates);
      }
      return fetchFresh();
    }

    function convert(amount, from, to) {
      if (amount == null) return null;
      from = (from || 'USD').toUpperCase();
      to   = (to   || getDisplayCurrency()).toUpperCase();
      if (from === to) return parseFloat(amount);
      // Normalize to USD first
      var inUsd = (from === 'USD') ? parseFloat(amount)
                : (rates[from] ? parseFloat(amount) / rates[from] : parseFloat(amount));
      // Then to target
      return (to === 'USD') ? inUsd
           : (rates[to] ? inUsd * rates[to] : inUsd);
    }

    return {
      init:      init,
      convert:   convert,
      symbol:    ccySymbol,
      rates:     function(){ return rates; },
      isLoaded:  function(){ return loaded; },
      displayCurrency: getDisplayCurrency
    };
  })();

  // Fire off the FX bootstrap immediately. Doesn't block — anything
  // rendered before rates arrive uses USD, which is the correct floor.
  FX.init();

  var fmt = {
    /**
     * Format a USD-denominated value in the user's display currency.
     * If the display currency != USD and rates are loaded, converts
     * using the cached rate. Otherwise renders the USD amount with a
     * USD sign — no symbol lying.
     */
    currency: function(v, dp) {
      dp = dp == null ? 2 : dp;
      if (v == null) return '\u2014';
      var disp = getDisplayCurrency();
      if (disp === 'USD' || !FX.isLoaded()) {
        return '$' + parseFloat(v).toFixed(dp);
      }
      var converted = FX.convert(parseFloat(v), 'USD', disp);
      return ccySymbol(disp) + converted.toFixed(dp);
    },
    money: function(v, dp) {
      dp = dp == null ? 4 : dp;
      if (v == null) return '\u2014';
      var disp = getDisplayCurrency();
      if (disp === 'USD' || !FX.isLoaded()) {
        return '$' + parseFloat(v).toFixed(dp);
      }
      var converted = FX.convert(parseFloat(v), 'USD', disp);
      return ccySymbol(disp) + converted.toFixed(dp);
    },
    moneyShort: function(v) {
      if (v == null) return '\u2014';
      var disp = getDisplayCurrency();
      if (disp === 'USD' || !FX.isLoaded()) {
        return '$' + parseFloat(v).toFixed(2);
      }
      var converted = FX.convert(parseFloat(v), 'USD', disp);
      return ccySymbol(disp) + converted.toFixed(2);
    },
    number:   function(v)    { return v!=null ? Number(v).toLocaleString() : '0'; },
    num:      function(v)    { return v!=null ? Number(v).toLocaleString() : '0'; },
    pct:      function(v)    { return v!=null ? parseFloat(v).toFixed(1) + '%' : '\u2014'; },
    date:     function(v)    { return v ? new Date(v).toLocaleDateString() : '\u2014'; },
    status: function(s) {
      var map = { active:'badge-green', approved:'badge-green', completed:'badge-green', draft:'badge-gray', archived:'badge-gray', pending:'badge-amber', paused:'badge-amber', rejected:'badge-red', suspended:'badge-red' };
      return '<span class="badge ' + (map[s]||'badge-gray') + '">' + esc(s) + '</span>';
    },
    badge: function(v, color) {
      var cls = { green:'badge-green', amber:'badge-amber', red:'badge-red', gray:'badge-gray', blue:'badge-blue', brand:'badge-brand' };
      return '<span class="badge ' + (cls[color]||'badge-gray') + '">' + esc(String(v)) + '</span>';
    }
  };

  // ── CM.upload — drag-drop / click file uploader (HOTFIX_73) ───────────
  // Reusable across creatives, attachments, datasets, etc. Validates
  // client-side against limits returned from /me/preferences.upload_limits
  // so the user gets immediate feedback instead of a server 422.
  //
  // Usage:
  //   1. Render: container.innerHTML = CM.upload.field({
  //        id: 'creative-image', label: 'Banner image',
  //        accept: 'image/*',
  //        maxMb: 5, mimeTypes: ['image/jpeg','image/png',...],
  //        helper: 'JPG, PNG, GIF or WebP'
  //      });
  //   2. Wire:  CM.upload.bind('creative-image', opts);
  //   3. On submit:
  //        var fd = new FormData();
  //        fd.append('campaign_id', ...);
  //        fd.append('image', document.getElementById('creative-image').files[0]);
  //        CM.upload.submit('/advertiser/creatives', fd).then(...)
  var Upload = {
    // Build the DOM markup for a single drop-zone uploader.
    field: function(opts) {
      var id      = String(opts.id || ('cm-up-'+Date.now()));
      var label   = opts.label  || 'Upload file';
      var accept  = opts.accept || '';
      var helper  = opts.helper || '';
      var maxMb   = opts.maxMb  || 10;
      var optional = opts.optional ? "<span style='color:#999;font-weight:400'>(optional)</span>" : "<span style='color:#dc2626'>*</span>";
      return ""
        + "<div class='form-group cm-upload-wrap' data-cm-upload='"+esc(id)+"'>"
        +   "<label class='form-label'>"+esc(label)+" "+optional+"</label>"
        +   "<div class='cm-upload-zone' style='border:2px dashed #d1d5db;border-radius:8px;padding:24px;text-align:center;background:#fafafa;cursor:pointer;transition:all .15s ease' onclick=\"document.getElementById('"+esc(id)+"').click()\">"
        +     "<i class='fas fa-cloud-upload-alt' style='font-size:32px;color:#990000;margin-bottom:8px;display:block'></i>"
        +     "<div style='font-size:14px;color:#374151;font-weight:600'>Drop file here or click to browse</div>"
        +     "<div style='font-size:11px;color:#6b7280;margin-top:6px'>"+esc(helper)+" &middot; max "+maxMb+"MB</div>"
        +     "<input type='file' id='"+esc(id)+"' accept='"+esc(accept)+"' style='display:none'>"
        +   "</div>"
        +   "<div class='cm-upload-preview' style='display:none;margin-top:8px;padding:10px 12px;background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;font-size:13px'></div>"
        + "</div>";
    },

    // Wire up the drop-zone events for a previously-rendered field.
    // Idempotent — safe to re-bind if the field is re-rendered.
    bind: function(id, opts) {
      opts = opts || {};
      var input = document.getElementById(id);
      if (!input) return;
      var wrap = input.closest('.cm-upload-wrap');
      if (!wrap || wrap.dataset.cmBound === '1') return;
      wrap.dataset.cmBound = '1';
      var zone = wrap.querySelector('.cm-upload-zone');
      var prev = wrap.querySelector('.cm-upload-preview');

      function handleFile(file) {
        var err = Upload.validate(file, opts);
        if (err) { toast(err, 'error'); input.value = ''; return; }
        // Show preview
        var sizeKb = (file.size / 1024).toFixed(1);
        var sizeStr = sizeKb >= 1024 ? (sizeKb/1024).toFixed(2)+' MB' : sizeKb+' KB';
        prev.style.display = 'flex';
        prev.style.alignItems = 'center';
        prev.style.justifyContent = 'space-between';
        prev.innerHTML =
          "<div style='display:flex;align-items:center;gap:8px;overflow:hidden'>"
          + "<i class='fas fa-file' style='color:#16a34a'></i>"
          + "<div style='overflow:hidden;text-overflow:ellipsis;white-space:nowrap'>"
          +   "<strong>"+esc(file.name)+"</strong>"
          +   "<span style='color:#6b7280;margin-left:8px;font-size:12px'>"+sizeStr+"</span>"
          + "</div></div>"
          + "<button type='button' style='background:none;border:none;color:#dc2626;cursor:pointer;font-weight:600;font-size:12px' onclick=\"CM.upload.clear('"+esc(id)+"')\">Remove</button>";
        zone.style.display = 'none';
        if (typeof opts.onChange === 'function') opts.onChange(file);
      }

      // Click handled via the inline onclick on the zone (delegates to file input).
      input.addEventListener('change', function(){
        if (input.files && input.files[0]) handleFile(input.files[0]);
      });

      // Drag-drop on the zone
      ['dragenter','dragover'].forEach(function(ev){
        zone.addEventListener(ev, function(e){
          e.preventDefault(); e.stopPropagation();
          zone.style.background = '#ffe6e6';
          zone.style.borderColor = '#990000';
        });
      });
      ['dragleave','drop'].forEach(function(ev){
        zone.addEventListener(ev, function(e){
          e.preventDefault(); e.stopPropagation();
          zone.style.background = '#fafafa';
          zone.style.borderColor = '#d1d5db';
        });
      });
      zone.addEventListener('drop', function(e){
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
          input.files = e.dataTransfer.files;
          handleFile(e.dataTransfer.files[0]);
        }
      });
    },

    // Pure validation — returns an error string or null. Reusable.
    validate: function(file, opts) {
      if (!file) return 'No file selected.';
      opts = opts || {};
      var maxMb    = opts.maxMb || 10;
      var maxBytes = maxMb * 1024 * 1024;
      if (file.size > maxBytes) {
        return "File too large (" + (file.size/1024/1024).toFixed(2) + " MB) — max is " + maxMb + " MB.";
      }
      if (Array.isArray(opts.mimeTypes) && opts.mimeTypes.length) {
        if (opts.mimeTypes.indexOf(file.type) === -1) {
          var nice = opts.mimeTypes.map(function(m){ return m.split('/').pop().toUpperCase(); }).join(', ');
          return "File type '"+(file.type||'unknown')+"' not allowed. Allowed: "+nice+".";
        }
      }
      return null;
    },

    // Reset a field to its empty state.
    clear: function(id) {
      var input = document.getElementById(id);
      if (!input) return;
      input.value = '';
      var wrap = input.closest('.cm-upload-wrap');
      if (!wrap) return;
      var zone = wrap.querySelector('.cm-upload-zone');
      var prev = wrap.querySelector('.cm-upload-preview');
      if (zone) zone.style.display = '';
      if (prev) { prev.style.display = 'none'; prev.innerHTML = ''; }
    },

    // Read the file currently selected in a field (or null).
    file: function(id) {
      var input = document.getElementById(id);
      return (input && input.files && input.files[0]) || null;
    },

    // Submit a multipart FormData. Mirrors the api() error handling so
    // callers get the same {success, data, error} contract.
    submit: async function(path, formData) {
      var headers = {};
      if (Auth.token) headers['Authorization'] = 'Bearer ' + Auth.token;
      try {
        var res  = await fetch(path, { method:'POST', headers: headers, body: formData });
        var data = await res.json();
        if (res.status === 401) { Auth.clear(); window.location.href = '/login'; return null; }
        return data;
      } catch (e) { return { success:false, error:{ message:'Network error during upload.' } }; }
    }
  };

  var pub = { Auth:Auth, api:api, get:get, post:post, patch:patch, del:del, toast:toast, openModal:openModal, closeModal:closeModal, esc:esc, setLoading:setLoading, fmt:fmt, fx:FX, upload:Upload };
  return pub;
})();

window.CM = CM;

// Auto-render the impersonation banner on every page load.  Runs after the
// DOM is parsed so document.body exists.  Idempotent.
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { CM.Auth._renderImpersonationBanner(); });
  } else {
    CM.Auth._renderImpersonationBanner();
  }
}


// ─────────────────────────────────────────────────────────────────────────
// HOTFIX_51 — Mobile navigation
// Injects a hamburger button into whichever topbar is present and wires
// drawer / dropdown toggle logic. Idempotent — safe to call twice.
// ─────────────────────────────────────────────────────────────────────────
(function initMobileNav(){
  function makeHamburger() {
    var btn = document.createElement('button');
    btn.className = 'mobile-menu-btn';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Toggle menu');
    // HOTFIX_54: FontAwesome v5 bars (was inline SVG)
    btn.innerHTML = '<i class="fas fa-bars" aria-hidden="true"></i>';
    return btn;
  }

  function ensureBackdrop() {
    var bd = document.querySelector('.mobile-drawer-backdrop');
    if (bd) return bd;
    bd = document.createElement('div');
    bd.className = 'mobile-drawer-backdrop';
    document.body.appendChild(bd);
    return bd;
  }

  // Sets up an off-canvas drawer (left sidebar pattern).
  // breakpoint = the max-width at which we treat as mobile (default 900).
  function setupDrawer(sidebarSel, topbarSel, breakpoint) {
    breakpoint = breakpoint || 900;
    var sidebar = document.querySelector(sidebarSel);
    var topbar  = document.querySelector(topbarSel);
    if (!sidebar || !topbar) return;
    if (topbar.querySelector('.mobile-menu-btn')) return; // idempotent

    var btn = makeHamburger();
    topbar.insertBefore(btn, topbar.firstChild);
    var backdrop = ensureBackdrop();

    function open() {
      sidebar.classList.add('is-open');
      backdrop.classList.add('is-open');
      document.body.classList.add('mobile-drawer-open');
    }
    function close() {
      sidebar.classList.remove('is-open');
      backdrop.classList.remove('is-open');
      document.body.classList.remove('mobile-drawer-open');
    }

    btn.addEventListener('click', function(){
      sidebar.classList.contains('is-open') ? close() : open();
    });
    backdrop.addEventListener('click', close);
    document.addEventListener('keydown', function(e){
      if (e.key === 'Escape' && sidebar.classList.contains('is-open')) close();
    });
    // Auto-close on nav-item / link clicks (so user lands on new section)
    sidebar.addEventListener('click', function(e){
      if (window.innerWidth > breakpoint) return;
      var t = e.target.closest('a, .nav-item, button');
      if (t && !t.classList.contains('mobile-menu-btn')) close();
    });
    window.addEventListener('resize', function(){
      if (window.innerWidth > breakpoint && sidebar.classList.contains('is-open')) close();
    });
  }

  // Sets up a drop-down menu (public site-nav pattern).
  function setupSiteNav() {
    var header = document.querySelector('.site-header');
    var nav    = document.querySelector('.site-nav');
    if (!header || !nav) return;
    if (header.querySelector('.mobile-menu-btn')) return;

    var btn = makeHamburger();
    var inner = header.querySelector('.site-header-inner') || header;
    inner.appendChild(btn);

    function open()  { nav.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true'); }
    function close() { nav.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false'); }

    btn.addEventListener('click', function(e){
      e.stopPropagation();
      nav.classList.contains('is-open') ? close() : open();
    });
    document.addEventListener('keydown', function(e){
      if (e.key === 'Escape' && nav.classList.contains('is-open')) close();
    });
    nav.addEventListener('click', function(e){
      if (window.innerWidth > 768) return;
      var t = e.target.closest('a');
      if (t) close();
    });
    // Click outside header → close
    document.addEventListener('click', function(e){
      if (window.innerWidth > 768) return;
      if (!nav.classList.contains('is-open')) return;
      if (!header.contains(e.target)) close();
    });
    window.addEventListener('resize', function(){
      if (window.innerWidth > 768 && nav.classList.contains('is-open')) close();
    });
  }

  function init() {
    // Dashboards: admin / publisher / advertiser / settings, etc.
    setupDrawer('.dash-sidebar', '.dash-topbar', 900);
    // Ad marketplace
    setupDrawer('.mkt-filters',  '.mkt-topbar',  900);
    // Data marketplace
    setupDrawer('.dmkt-sidebar', '.dmkt-topbar', 900);
    // Public pages (top nav)
    setupSiteNav();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  }
})();
